import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { leadService } from '../services/leads'
import type { PreviousPolicy } from '../../../types'
import { History, ExternalLink } from 'lucide-react'

interface Props {
  leadId: number
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>
        {label}
      </span>
      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function PreviousPolicyCard({ leadId }: Props) {
  const [history, setHistory] = useState<PreviousPolicy[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    leadService.getPreviousPolicy(leadId)
      .then(data => { if (!cancelled) setHistory(data) })
      .catch(() => { if (!cancelled) setHistory([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [leadId])

  if (loading || history.length === 0) return null

  return (
    <div className="card">
      <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
        <History className="h-4 w-4" /> Policy History (issued by us)
      </h3>
      <div className="flex flex-col gap-3">
        {history.map((policy, i) => (
          <div
            key={policy.lead_id}
            className="flex flex-col gap-2"
            style={{
              paddingBottom: i < history.length - 1 ? 12 : 0,
              borderBottom: i < history.length - 1 ? '1px solid var(--border-default)' : undefined,
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                {policy.policy_issued_at ? formatDate(policy.policy_issued_at) : `Lead #${policy.lead_id}`}
              </span>
              <Link
                to={`/leads/${policy.lead_id}`}
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--brand-primary)' }}
              >
                View lead <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            {policy.policy_number && <DetailRow label="Policy No." value={policy.policy_number} />}
            {policy.insurance_company && <DetailRow label="Insurer" value={policy.insurance_company} />}
            {policy.premium_amount != null && (
              <DetailRow label="Premium" value={`₹${policy.premium_amount.toLocaleString('en-IN')}`} />
            )}
            {policy.od_discount_percentage != null && (
              <DetailRow label="OD Discount" value={`${policy.od_discount_percentage}%`} />
            )}
            {policy.insurance_due_date && (
              <DetailRow label="Was Due" value={formatDate(policy.insurance_due_date)} />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
